/* ════════════════════════════════════════════════════════════
   SPA case-study renderer + scroll observer.
   Builds one .cs-section per PORTFOLIO_PROJECTS entry, with
   placeholder bento content. Exposes:
     window.__caseStudyScrollTo(slug, behavior?) — used by the
       router to position the scroller on entry.
     window.__caseStudySlugChanged(slug)         — set by the
       router so this IIFE can keep its currentRoute in sync.
   The scroll observer updates the URL via history.replaceState
   and notifies the router so the back button still goes /work.
   ════════════════════════════════════════════════════════════ */
'use strict';

const view     = document.getElementById('view-case-study');
const scroller = document.getElementById('cs-scroller');
const closeBtn = document.getElementById('cs-close');
/* Top-level early-return isn't allowed in modules, so wrap the
   rest in a guard. Same semantic as the original IIFE's bail-out. */
if (view && scroller) {

  function escapeAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /* Defer rendering until project data is available. */
  (window.__projectsReady || Promise.resolve([])).then(function (data) {
    if (!data || !data.length) return;

  /* ════════════════════════════════════════════════════════════
     renderCaseStudy(project, options?) — pure: data in, HTML out.

     Project shape (from YAML frontmatter):
       {
         slug, project, company, type, year,    // metadata
         accent?: "#hex",                       // optional accent
         sections: [
           { type: 'hero',        src, alt? },
           { type: 'heading',     level?: 1|2, text },
           { type: 'text',        text | body, html?: true },
           { type: 'html',        html | content | body | text },
           { type: 'image',       src, alt?, caption? },
           { type: 'image-full',  src, alt? },
           { type: 'image-pair',  items: [{src, alt?, caption?}, ...] },
           { type: 'image-grid',  columns?: 2|3|4, items: [{src, alt?}, ...] },
           { type: 'video',       src, poster?, autoplay?, loop?, muted?,
                                  controls?: bool, caption? },
           { type: 'video-pair',  items: [{src, autoplay?, loop?, muted?,
                                            poster?, caption?}, ...] },
           { type: 'scroll-video', src, width?: 'contained'|'full',
                                   background?: '#hex', alt?,
                                   start_offset?: px, end_offset?: px },
           { type: 'quote',       text, author?, role? },
           { type: 'stats',       items: [{value, label}, ...] },
           { type: 'spacer',      size?: 'small'|'medium'|'large' },
           { type: 'intro',       summary?, role?,
                                  team?: string | string[],
                                  deliverables?: string | string[] },
         ]
       }

     Each section maps 1:1 to a class from the case-study design-
     token stylesheet. Unknown types are silently dropped.
     ════════════════════════════════════════════════════════════ */
  function renderCaseStudy(project, options) {
    if (!project || typeof project !== 'object') return '';
    const esc = escapeAttr;
    const opts = options || {};
    const sections = Array.isArray(project.sections) ? project.sections : [];

    /* width/height attrs from build-time image-size enrichment.
       Reserves correct aspect-ratio space before the image bytes
       arrive — prevents lazy-load reflow from shifting scroll
       targets out from under the FLIP animation. CSS still controls
       the rendered size (cs-image img { width:100%; height:auto }
       and friends), so attrs only affect placeholder geometry. */
    function dimsAttr(o) {
      if (!o) return '';
      const w = Number(o.width);
      const h = Number(o.height);
      if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return '';
      return ' width="' + w + '" height="' + h + '"';
    }

    function renderHero(s) {
      return ''
        + '<div class="cs-hero">'
        +   '<img src="' + esc(s.src) + '" alt="' + esc(s.alt || project.project || '') + '"' + dimsAttr(s) + ' loading="lazy" decoding="async">'
        + '</div>';
    }

    function renderHeading(s) {
      const level = s.level === 1 ? 1 : 2;
      const tag = 'h' + level;
      const cls = 'cs-heading h' + level;
      return '<' + tag + ' class="' + cls + '">' + esc(s.text || '') + '</' + tag + '>';
    }

    function renderText(s) {
      const body = s.text != null ? s.text : (s.body != null ? s.body : '');
      if (s.html) {
        return '<div class="cs-text">' + body + '</div>';
      }
      const paragraphs = String(body)
        .split(/\n{2,}/)
        .map(function (p) { return p.trim(); })
        .filter(Boolean)
        .map(function (p) { return '<p>' + esc(p) + '</p>'; })
        .join('');
      return '<div class="cs-text">' + paragraphs + '</div>';
    }

    function renderHtml(s) {
      // Raw HTML escape hatch. Content is inserted verbatim — author is
      // responsible for safety. Sits inside .cs-html, which is constrained
      // to the case-study content column (same max-width as .cs-text/.cs-image).
      const body = s.html != null ? s.html
                 : s.content != null ? s.content
                 : s.body != null ? s.body
                 : s.text != null ? s.text
                 : '';
      return '<div class="cs-html">' + String(body) + '</div>';
    }

    function renderImage(s) {
      // Optional maxWidth caps the image's width (centered). Accepts a
      // number (px) or any CSS length string. Unset = image fills the
      // content column as before.
      const capVal = normalizeMaxWidth(
        s.maxWidth != null ? s.maxWidth : s['max-width']
      );
      const cap = s.caption
        ? '<figcaption class="cs-caption">' + esc(s.caption) + '</figcaption>'
        : '';
      const tag = cap ? 'figure' : 'div';
      const cls = 'cs-image' + (capVal ? ' cs-image--capped' : '');
      const style = capVal
        ? ' style="--cs-image-max:' + esc(capVal) + '"'
        : '';
      return ''
        + '<' + tag + ' class="' + cls + '"' + style + '>'
        +   '<img src="' + esc(s.src) + '" alt="' + esc(s.alt || '') + '"' + dimsAttr(s) + ' loading="lazy" decoding="async">'
        +   cap
        + '</' + tag + '>';
    }

    function renderImageFull(s) {
      // Optional maxWidth:
      //   • number / CSS length ("1200", "80rem") → cap at that width, centered.
      //   • "natural" / "auto" / true             → cap at the image's intrinsic
      //                                              size — never enlarge past it.
      //   • unset                                 → full-bleed 100vw (default).
      const raw = s.maxWidth != null ? s.maxWidth : s['max-width'];
      const isNatural = raw === true
        || (typeof raw === 'string' && /^(natural|auto)$/i.test(raw.trim()));

      let cap = '';
      if (!isNatural && raw != null && raw !== '') {
        cap = typeof raw === 'number' ? raw + 'px' : String(raw);
      }

      let cls = 'cs-image-full';
      let style = '';
      if (isNatural) {
        cls += ' cs-image-full--natural';
      } else if (cap) {
        cls += ' cs-image-full--capped';
        style = ' style="--cs-image-full-max:' + esc(cap) + '"';
      }

      const captionHtml = s.caption
        ? '<figcaption class="cs-caption">' + esc(s.caption) + '</figcaption>'
        : '';
      const tag = s.caption ? 'figure' : 'div';
      return ''
        + '<' + tag + ' class="' + cls + '"' + style + '>'
        +   '<img src="' + esc(s.src) + '" alt="' + esc(s.alt || '') + '"' + dimsAttr(s) + ' loading="lazy" decoding="async">'
        +   captionHtml
        + '</' + tag + '>';
    }

    function renderImagePair(s) {
      const items = Array.isArray(s.items) ? s.items.slice(0, 2) : [];
      const cells = items.map(function (it) {
        const cap = it.caption
          ? '<figcaption class="cs-caption">' + esc(it.caption) + '</figcaption>'
          : '';
        const tag = cap ? 'figure' : 'div';
        return ''
          + '<' + tag + '>'
          +   '<img src="' + esc(it.src) + '" alt="' + esc(it.alt || '') + '"' + dimsAttr(it) + ' loading="lazy" decoding="async">'
          +   cap
          + '</' + tag + '>';
      }).join('');
      return '<div class="cs-image-pair">' + cells + '</div>';
    }

    function renderImageGrid(s) {
      // By default, cells respect each image's natural aspect ratio
      // (no cropping). Set `crop: true` to clip every cell to a uniform
      // aspect ratio (legacy behavior). Override that ratio with
      // `aspectRatio` (e.g. "4/3", "1/1", or a number like 1.5).
      const items = Array.isArray(s.items) ? s.items : [];
      const cols = (s.columns === 2 || s.columns === 3 || s.columns === 4) ? s.columns : 3;

      const crop = s.crop === true;
      const ratio = s.aspectRatio != null ? s.aspectRatio : s['aspect-ratio'];
      const styleParts = ['grid-template-columns: repeat(' + cols + ', 1fr)'];
      if (crop && ratio != null && ratio !== '') {
        styleParts.push('--cs-image-grid-aspect: ' + esc(String(ratio)));
      }
      const inlineGrid = ' style="' + styleParts.join('; ') + ';"';

      const cls = 'cs-image-grid' + (crop ? ' cs-image-grid--crop' : '');
      const cells = items.map(function (it) {
        return ''
          + '<div>'
          +   '<img src="' + esc(it.src) + '" alt="' + esc(it.alt || '') + '"' + dimsAttr(it) + ' loading="lazy" decoding="async">'
          + '</div>';
      }).join('');
      return '<div class="' + cls + '"' + inlineGrid + '>' + cells + '</div>';
    }

    /* Bump this when video bytes on R2 are replaced. Browsers cache
       <video> fetches in a media-cache that is separate from the
       regular HTTP cache and is NOT invalidated by Cmd-Shift-R or
       even by toggling "Disable cache" in DevTools — only by URL
       change or a full settings → "Clear browsing data" sweep.
       Appending ?v=<bump> forces a fresh fetch the next time the
       client loads the page, so users never see stale broken bytes
       from a previous upload. Plain integer; no semantic meaning. */
    const VIDEO_CACHE_BUST = '20260619c';

    function videoSrc(v) {
      let src = String(v.src || '');

      /* Append the cache-buster as a query string. Skip data: URIs,
         blob: URIs, and anything that already carries ?v= (so authors
         can pin a different version per file when needed). */
      const isRemote = /^https?:\/\//i.test(src);
      if (isRemote && !/[?&]v=/.test(src)) {
        const qSep = src.indexOf('?') >= 0 ? '&' : '?';
        /* Query string must come BEFORE any Media Fragment (#t=...)
           because the fragment is part of the URL identifier on the
           client only — but if we put #t= before ?v=, parsers treat
           the ?v= as part of the fragment and the cache-buster never
           reaches the network. So we split on '#', insert ?v= into
           the path-and-query side, then re-join. */
        const hashIdx = src.indexOf('#');
        if (hashIdx >= 0) {
          src = src.slice(0, hashIdx) + qSep + 'v=' + VIDEO_CACHE_BUST
              + src.slice(hashIdx);
        } else {
          src = src + qSep + 'v=' + VIDEO_CACHE_BUST;
        }
      }

      /* If the author supplied a poster_time (seconds) but no poster
         image, append a Media Fragment hint so the browser paints
         the frame at that timestamp as the cover. Skipped when an
         explicit poster image is set, since that wins. */
      if (v.poster || v.poster_time == null) return src;
      const t = Number(v.poster_time);
      if (!isFinite(t) || t < 0) return src;
      const sep = src.indexOf('#') >= 0 ? '&' : '#';
      return src + sep + 't=' + t;
    }

    function videoAttrs(v) {
      const attrs = [];
      /* Tag videos that rely on a Media Fragment for their cover
         frame so we can rewind them to 0 on first play (otherwise
         playback would resume from the poster timestamp). */
      if (!v.poster && v.poster_time != null) {
        attrs.push('data-rewind-on-play="1"');
      }
      if (v.poster)    attrs.push('poster="' + esc(v.poster) + '"');
      /* Lazy-loading: we DO NOT emit the `autoplay` attribute up-
         front, because that forces the browser to start downloading
         immediately regardless of `preload`. Instead, stash intent
         in data-autoplay; setupLazyVideos() promotes the element
         when it enters the viewport. Same story for `preload`: we
         start at "none" (zero bytes fetched) and bump it to the
         target value (metadata for posters, auto for autoplay) on
         intersection. This was the fix for randomly-failing videos
         on the deployed site — 6-8 videos racing range requests
         against the latency to R2 caused some to time out. */
      if (v.autoplay)  attrs.push('data-autoplay="1"');
      if (v.loop)      attrs.push('loop');
      if (v.muted || v.autoplay) attrs.push('muted'); /* autoplay implies muted */
      if (v.autoplay)  attrs.push('playsinline');
      /* Show native controls by default; hide only when the author
         explicitly opts out OR when the video autoplays silently
         (looping background-style demos). */
      const showControls = v.controls === false ? false
        : (v.controls === true ? true : !v.autoplay);
      if (showControls) attrs.push('controls');
      /* Target preload: autoplay clips need enough buffered to start
         playing the moment they enter the viewport, so they get
         "auto"; everything else just needs metadata for the first
         frame. setupLazyVideos() reads data-preload-target. */
      const targetPreload = v.autoplay ? 'auto' : 'metadata';
      attrs.push('preload="none"');
      attrs.push('data-preload-target="' + targetPreload + '"');
      return attrs.join(' ');
    }

    function renderVideoFull(s) {
      // Mirrors renderImageFull. Optional maxWidth:
      //   • number / CSS length ("1200", "80rem") → cap at that width, centered.
      //   • "natural" / "auto" / true             → cap at the video's intrinsic
      //                                              size — never enlarge past it.
      //   • unset                                 → full-bleed 100vw (default).
      const raw = s.maxWidth != null ? s.maxWidth : s['max-width'];
      const isNatural = raw === true
        || (typeof raw === 'string' && /^(natural|auto)$/i.test(raw.trim()));

      let cap = '';
      if (!isNatural && raw != null && raw !== '') {
        cap = typeof raw === 'number' ? raw + 'px' : String(raw);
      }

      let cls = 'cs-video-full';
      let style = '';
      if (isNatural) {
        cls += ' cs-video-full--natural';
      } else if (cap) {
        cls += ' cs-video-full--capped';
        style = ' style="--cs-video-full-max:' + esc(cap) + '"';
      }

      const captionHtml = s.caption
        ? '<figcaption class="cs-caption">' + esc(s.caption) + '</figcaption>'
        : '';
      const tag = s.caption ? 'figure' : 'div';
      return ''
        + '<' + tag + ' class="' + cls + '"' + style + '>'
        +   '<video src="' + esc(videoSrc(s)) + '" ' + videoAttrs(s) + '></video>'
        +   captionHtml
        + '</' + tag + '>';
    }

    function renderVideo(s) {
      // Optional maxWidth caps the video's width (centered). Accepts a
      // number (px) or any CSS length string. Unset = fills content
      // column as before.
      const capVal = normalizeMaxWidth(
        s.maxWidth != null ? s.maxWidth : s['max-width']
      );
      const cap = s.caption
        ? '<figcaption class="cs-caption">' + esc(s.caption) + '</figcaption>'
        : '';
      const tag = cap ? 'figure' : 'div';
      const cls = 'cs-video' + (capVal ? ' cs-video--capped' : '');
      const style = capVal
        ? ' style="--cs-video-max:' + esc(capVal) + '"'
        : '';
      return ''
        + '<' + tag + ' class="' + cls + '"' + style + '>'
        +   '<video src="' + esc(videoSrc(s)) + '" ' + videoAttrs(s) + '></video>'
        +   cap
        + '</' + tag + '>';
    }

    const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
    function safeOffsetPx(v) {
      const n = Number(v);
      return isFinite(n) && n > 0 ? Math.floor(n) : 0;
    }
    function renderScrollVideo(s) {
      const isFull = s.width === 'full';
      const cls = 'cs-scroll-video' + (isFull ? ' cs-scroll-video-full' : '');
      /* background accepts a hex code only (#RGB / #RRGGBB / #RRGGBBAA);
         anything else is silently dropped so the video's own alpha
         channel shows through to the page. */
      const bgVal = typeof s.background === 'string' ? s.background.trim() : '';
      const bg = bgVal && HEX_RE.test(bgVal)
        ? ' style="background:' + bgVal + '"'
        : '';
      const alt = s.alt ? ' aria-label="' + esc(s.alt) + '"' : '';
      const startOff = safeOffsetPx(s.start_offset);
      const endOff   = safeOffsetPx(s.end_offset);
      const offAttrs =
            (startOff ? ' data-scroll-start="' + startOff + '"' : '')
          + (endOff   ? ' data-scroll-end="'   + endOff   + '"' : '');
      /* Scroll-video uses preload="none" up-front (same lazy
         strategy as regular videos) but with a wider rootMargin
         in setupLazyVideos so it's promoted to "auto" well before
         the user starts scrubbing. data-preload-target="auto"
         signals the promoter to fully buffer the file. */
      return ''
        + '<div class="' + cls + '"' + bg + alt + '>'
        +   '<video'
        +     ' src="' + esc(videoSrc(s)) + '"'
        +     ' muted playsinline preload="none"'
        +     ' data-preload-target="auto"'
        +     ' disablepictureinpicture'
        +     ' data-scroll-video="1"' + offAttrs
        +   '></video>'
        + '</div>';
    }

    function renderVideoPair(s) {
      // Each video keeps its natural aspect ratio. Optional maxWidth caps
      // an individual video's width (centered in its column). Author may
      // set maxWidth per-item, or at the section level as a default for
      // both items. Accepts a number (px) or a CSS length string.
      const sectionCap = normalizeMaxWidth(
        s.maxWidth != null ? s.maxWidth : s['max-width']
      );
      const items = Array.isArray(s.items) ? s.items.slice(0, 2) : [];
      const cells = items.map(function (it) {
        const cap = it.caption
          ? '<figcaption class="cs-caption">' + esc(it.caption) + '</figcaption>'
          : '';
        const tag = cap ? 'figure' : 'div';
        const itemCap = normalizeMaxWidth(
          it.maxWidth != null ? it.maxWidth : it['max-width']
        ) || sectionCap;
        const style = itemCap
          ? ' style="--cs-video-pair-max:' + esc(itemCap) + '"'
          : '';
        const cls = itemCap ? ' class="cs-video-pair-cell--capped"' : '';
        return ''
          + '<' + tag + cls + style + '>'
          +   '<video src="' + esc(videoSrc(it)) + '" ' + videoAttrs(it) + '></video>'
          +   cap
          + '</' + tag + '>';
      }).join('');
      return '<div class="cs-video vertical-pair">' + cells + '</div>';
    }

    function normalizeMaxWidth(raw) {
      if (raw == null || raw === '') return '';
      if (typeof raw === 'number') return raw + 'px';
      return String(raw).trim();
    }

    function renderQuote(s) {
      const attribution = (s.author || s.role)
        ? '<cite>'
        +   (s.author ? esc(s.author) : '')
        +   (s.author && s.role ? ', ' : '')
        +   (s.role ? esc(s.role) : '')
        + '</cite>'
        : '';
      return ''
        + '<blockquote class="cs-quote">'
        +   '<span>' + esc(s.text || '') + '</span>'
        +   attribution
        + '</blockquote>';
    }

    function renderStats(s) {
      const items = Array.isArray(s.items) ? s.items : [];
      const cells = items.map(function (it) {
        return ''
          + '<div class="cs-stat">'
          +   '<div class="cs-stat-value">' + esc(it.value || '') + '</div>'
          +   '<div class="cs-stat-label">' + esc(it.label || '') + '</div>'
          + '</div>';
      }).join('');
      return '<div class="cs-stats">' + cells + '</div>';
    }

    function renderSpacer(s) {
      const size = (s.size === 'small' || s.size === 'medium' || s.size === 'large')
        ? s.size
        : 'medium';
      return '<div class="cs-spacer ' + size + '" aria-hidden="true"></div>';
    }

    /* Intro: two-card glass panel sitting between the auto-injected
       cs-meta header and the rest of the body. The narrative card
       shows About + My role; the meta card shows Year/Company/Product
       (pulled from top-level project fields) and Deliverables (this
       section's own field). Every field is optional; the whole block
       is suppressed if everything is empty. */
    function renderIntro(s) {
      const metaParts = [];
      if (project.year)    metaParts.push(['Year',    project.year]);
      if (project.company) metaParts.push(['Company', project.company]);
      if (project.type)    metaParts.push(['Product', project.type]);
      if (s.team) {
        const t = Array.isArray(s.team)
          ? s.team.join(', ')
          : String(s.team);
        if (t) metaParts.push(['Team', t]);
      }
      if (s.deliverables) {
        const d = Array.isArray(s.deliverables)
          ? s.deliverables.join(', ')
          : String(s.deliverables);
        if (d) metaParts.push(['Deliverables', d]);
      }
      if (!s.summary && !s.role && metaParts.length === 0) return '';

      const metaRows = metaParts.map(function (kv) {
        return ''
          + '<div class="cs-intro-meta-row">'
          +   '<dt>' + esc(kv[0]) + '</dt>'
          +   '<dd>' + esc(kv[1]) + '</dd>'
          + '</div>';
      }).join('');

      const edgeLight = '<span class="edge-light" aria-hidden="true"></span>';

      return ''
        + '<div class="cs-intro">'
        +   '<div class="cs-intro-card cs-intro-narrative">'
        +     edgeLight
        +     (s.summary
              ? '<section><h3>About the project</h3><p>' + esc(s.summary) + '</p></section>'
              : '')
        +     (s.role
              ? '<section><h3>My role</h3><p>' + esc(s.role) + '</p></section>'
              : '')
        +   '</div>'
        +   (metaRows
              ? '<aside class="cs-intro-card cs-intro-meta">'
              +   edgeLight
              +   '<dl>' + metaRows + '</dl>'
              + '</aside>'
              : '')
        + '</div>';
    }

    function renderMeta() {
      if (!project.project) return '';
      return ''
        + '<header class="cs-meta">'
        +   '<h1 class="cs-title">' + esc(project.project) + '</h1>'
        +   (project.subtitle
              ? '<p class="cs-subtitle">' + esc(project.subtitle) + '</p>'
              : '')
        + '</header>';
    }

    const RENDERERS = {
      'hero':        renderHero,
      'heading':     renderHeading,
      'text':        renderText,
      'html':        renderHtml,
      'image':       renderImage,
      'image-full':  renderImageFull,
      'image-pair':  renderImagePair,
      'image-grid':  renderImageGrid,
      'video':       renderVideo,
      'video-full':  renderVideoFull,
      'video-pair':  renderVideoPair,
      'scroll-video': renderScrollVideo,
      'quote':       renderQuote,
      'stats':       renderStats,
      'spacer':      renderSpacer,
      'intro':       renderIntro,
    };

    // Build body. Inject the .cs-meta header (project name + sub
    // metadata) right after the first hero section, since the hero
    // is always the first thing the user sees and the meta block
    // visually anchors to it. If there's no hero, prepend meta.
    let metaInjected = false;
    const parts = [];
    if (!sections.some(function (s) { return s && s.type === 'hero'; })) {
      const meta = renderMeta();
      if (meta) { parts.push(meta); metaInjected = true; }
    }
    for (let i = 0; i < sections.length; i++) {
      const s = sections[i];
      if (!s || !s.type) continue;
      const fn = RENDERERS[s.type];
      if (!fn) continue;

      // Pair an h2 heading with an immediately-following text block
      // into a two-column row: heading (30%) + paragraph (70%).
      const next = sections[i + 1];
      const isH2 = s.type === 'heading' && s.level !== 1;
      if (isH2 && next && next.type === 'text') {
        parts.push(
          '<div class="cs-heading-text-row">'
          + renderHeading(s)
          + renderText(next)
          + '</div>'
        );
        i++; // consume the paired text block
        continue;
      }

      parts.push(fn(s));
      if (!metaInjected && s.type === 'hero') {
        const meta = renderMeta();
        if (meta) parts.push(meta);
        metaInjected = true;
      }
    }
    const body = parts.join('');

    const accentVar = project.accent
      ? ' style="--cs-accent: ' + esc(project.accent) + ';"'
      : '';

    const nextName = opts.nextProject
      ? (opts.nextProject.project || opts.nextProject.company || '')
      : '';

    let nextLabel = '';
    if (opts.showNextLabel) {
      /* Own full-screen section so the JS snap engine treats it as a
         dedicated stop — users get a clean "breathing" screen between
         projects. data-slug carries the outgoing project's slug so the
         URL stays unchanged while the teaser is in view. */
      nextLabel = ''
        + '<section class="cs-section cs-teaser-section" data-slug="' + esc(project.slug || '') + '">'
        +   '<div class="cs-next-teaser" aria-hidden="true">'
        +     '<div class="cs-next-label">'
        +       'Next case study'
        +       (nextName ? ': <span class="cs-next-name">' + esc(nextName) + '</span>' : '')
        +     '</div>'
        +     '<div class="cs-next-cta">'
        +       '<span>Keep scrolling</span>'
        +       '<svg class="cs-next-arrow" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">'
        +         '<path d="M12 4v14m0 0l-6-6m6 6l6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
        +       '</svg>'
        +     '</div>'
        +   '</div>'
        + '</section>';
    } else if (opts.isLast) {
      nextLabel = ''
        + '<section class="cs-section cs-teaser-section cs-end-section" data-slug="' + esc(project.slug || '') + '">'
        +   '<div class="cs-next-teaser cs-end-teaser">'
        +     '<div class="cs-next-label">That\u2019s a wrap</div>'
        +     '<button type="button" class="cs-back-btn" data-action="back-to-portfolio">'
        +       '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">'
        +         '<path d="M19 12H5m0 0l6-6m-6 6l6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'
        +       '</svg>'
        +       '<span>Back to portfolio</span>'
        +     '</button>'
        +   '</div>'
        + '</section>';
    }

    return ''
      + '<section class="cs-section" data-slug="' + esc(project.slug || '') + '"' + accentVar + '>'
      +   '<div class="cs-inner">' + body + '</div>'
      + '</section>'
      + nextLabel;
  }
  /* Exposed for testing. */
  window.renderCaseStudy = renderCaseStudy;

  scroller.innerHTML = data.map(function (p, i) {
    const isLast = i === data.length - 1;
    return renderCaseStudy(p, {
      showNextLabel: !isLast,
      isLast: isLast,
      nextProject: isLast ? null : data[i + 1],
    });
  }).join('');

  /* The blur band, the scroll-reveal observer, and the video
     rewind-on-play wiring are all idle work when the case-study
     view is hidden — but the 30+ backdrop-filtered layers in the
     blur band still cost the compositor on every frame because
     the elements live in the DOM tree. Defer everything until the
     view actually becomes visible, then run once. */
  let csViewPrimed = false;
  function _isMobile() { return window.innerWidth <= 768; }

  function primeCaseStudyView() {
    if (csViewPrimed) return;
    csViewPrimed = true;

    /* On desktop everything is cheap — run synchronously. On
       mobile, spread the 5 setup calls across separate frames so
       iOS Safari doesn't see one giant synchronous workload right
       when the case-study DOM becomes visible. Each frame yields
       back to the compositor, giving the WebContent process time
       to release intermediate memory pressure instead of
       accumulating it (part of the same iOS-cap problem that
       motivated the mobile FLIP rewrite in src/flip.js). */
    if (!_isMobile()) {
      buildGradualBlur();
      setupScrollReveal();
      setupVideoRewind();
      setupLazyVideos();
      setupScrollVideos();
      return;
    }

    /* Mobile: one step per rAF. setupLazyVideos goes first because
       it gates all video bytes; buildGradualBlur goes last because
       it's purely visual polish. */
    const queue = [
      setupLazyVideos,
      setupScrollReveal,
      setupVideoRewind,
      setupScrollVideos,
      buildGradualBlur,
    ];
    function tick() {
      const next = queue.shift();
      if (!next) return;
      try { next(); } catch (e) { /* never block the queue */ }
      /* Two rAFs between steps = one paint of compositor work. */
      requestAnimationFrame(function () {
        requestAnimationFrame(tick);
      });
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(tick);
    });
  }
  /* Visibility = #view-case-study has display !== 'none'. SPA
     router toggles inline style.display, so we watch for that
     mutation. */
  if (typeof MutationObserver === 'function') {
    const csView = document.getElementById('view-case-study');
    if (csView) {
      const isVisible = function () { return csView.style.display !== 'none'; };
      if (isVisible()) {
        primeCaseStudyView();
      } else {
        const mo = new MutationObserver(function () {
          if (isVisible()) { primeCaseStudyView(); mo.disconnect(); }
        });
        mo.observe(csView, { attributes: true, attributeFilter: ['style'] });
      }
    }
  } else {
    primeCaseStudyView();
  }

  function buildGradualBlur() {
    /* Builds the stacked-layer gradient blur (a la react-bits) for
       every .cs-blur-inner host found inside the case-study view.
       Each host's parent .cs-blur-edge tells us whether it's the
       top or bottom band; the mask direction is flipped accordingly
       and the top band uses a lower strength so it doesn't compete
       with the hero. */
    const hosts = document.querySelectorAll('#view-case-study .cs-blur-inner');
    if (!hosts.length) return;
    const curves = {
      linear:        function (p) { return p; },
      bezier:        function (p) { return p * p * (3 - 2 * p); },
      'ease-in':     function (p) { return p * p; },
      'ease-out':    function (p) { return 1 - Math.pow(1 - p, 2); },
      'ease-in-out': function (p) { return p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; },
    };
    hosts.forEach(function (host) {
      if (host.children.length) return;
      const isTop = host.parentElement && host.parentElement.classList.contains('cs-blur-top');
      const config = {
        position: isTop ? 'top' : 'bottom',
        divCount: 6,
        /* Top band is shorter and shouldn't dominate the hero, so the
           overall blur strength is dialed back. */
        strength: isTop ? 2 : 3,
        curve: 'bezier',
        exponential: true,
        opacity: 1,
      };
      const curveFn = curves[config.curve] || curves.linear;
      const gradientDir = {
        top:    'to top',
        bottom: 'to bottom',
        left:   'to left',
        right:  'to right',
      }[config.position] || 'to bottom';
      const increment = 100 / config.divCount;

      const html = [];
      for (let i = 1; i <= config.divCount; i++) {
        const progress = curveFn(i / config.divCount);
        const blur = config.exponential
          ? Math.pow(2, progress * 4) * 0.0625 * config.strength
          : 0.0625 * (progress * config.divCount + 1) * config.strength;

        const p1 = Math.round((increment * i - increment) * 10) / 10;
        const p2 = Math.round((increment * i) * 10) / 10;
        const p3 = Math.round((increment * i + increment) * 10) / 10;
        const p4 = Math.round((increment * i + increment * 2) * 10) / 10;

        let stops = 'transparent ' + p1 + '%, black ' + p2 + '%';
        if (p3 <= 100) stops += ', black ' + p3 + '%';
        if (p4 <= 100) stops += ', transparent ' + p4 + '%';

        const mask = 'linear-gradient(' + gradientDir + ', ' + stops + ')';
        const blurCss = 'blur(' + blur.toFixed(3) + 'rem)';

        html.push(
          '<div style="' +
            'mask-image:' + mask + ';' +
            '-webkit-mask-image:' + mask + ';' +
            'backdrop-filter:' + blurCss + ';' +
            '-webkit-backdrop-filter:' + blurCss + ';' +
            'opacity:' + config.opacity + ';' +
          '"></div>'
        );
      }
      host.innerHTML = html.join('');
    });
  }

  /* Scroll-reveal: tag every body block (skip hero + next label),
     then fade + slide-up on first intersection. One observer for
     the whole scroller; unobserve after revealing so it only fires
     once per element. */
  function setupScrollReveal() {
    const blocks = scroller.querySelectorAll('.cs-inner > *');
    blocks.forEach(function (el) {
      if (el.classList.contains('cs-hero')) return;
      if (el.classList.contains('cs-next-label')) return;
      /* Intro cards have their own staggered entrance and rely on
         backdrop-filter, which a transform on the wrapper would
         break (parent transforms disable backdrop blur). */
      if (el.classList.contains('cs-intro')) return;
      el.classList.add('cs-reveal');
    });
    if (!('IntersectionObserver' in window)) {
      scroller.querySelectorAll('.cs-reveal').forEach(function (el) {
        el.classList.add('cs-revealed');
      });
      return;
    }
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('cs-revealed');
        io.unobserve(entry.target);
      });
    }, { root: scroller, rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    scroller.querySelectorAll('.cs-reveal').forEach(function (el) {
      io.observe(el);
    });
  }

  /* Videos that use poster_time (Media Fragment #t=…) paint the
     chosen frame as their cover, but the browser would otherwise
     start playback from that timestamp. Rewind to 0 on the first
     play so the user always sees the full clip from the beginning. */
  function setupVideoRewind() {
    scroller.querySelectorAll('video[data-rewind-on-play]').forEach(function (video) {
      function rewindOnce() {
        try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
        video.removeEventListener('play', rewindOnce);
        video.removeAttribute('data-rewind-on-play');
      }
      video.addEventListener('play', rewindOnce);
    });
  }

  /* Lazy-load every video. The renderers emit videos with
     preload="none" and (for autoplay clips) only data-autoplay="1"
     instead of the actual autoplay attribute, so the browser
     hasn't fetched a single byte at this point. We promote each
     video as it nears the viewport:

       1. Set the real `preload` (target stashed in data-preload-
          target — "metadata" for posters, "auto" for autoplay/
          scroll-videos that need to be ready to play immediately).
       2. For autoplay clips, set the `autoplay` attribute and call
          play() once we're at preload="auto" so the playback
          actually starts when the user can see it.

     rootMargin is generous so videos start loading well before
     they enter the viewport — scroll-videos get the widest margin
     since they need enough buffered for instant scrubbing. The
     observer is one-shot per video: once promoted, we unobserve.
     This was added to fix randomly-failing videos on the deployed
     site where 6-8 videos racing range requests over the latency
     to R2 caused some to time out. */
  /* Lazy-load every video, but SERIALIZE the loads so we never have
     more than MAX_CONCURRENT in flight. Earlier the IntersectionObserver
     promoted every video within 600px of the viewport simultaneously,
     which on case-study pages mounts 4-6 video range requests at the
     same instant. Cloudflare Pages → R2 has enough latency that 6
     concurrent requests stall some of them, leaving solid black boxes.
     The pattern was random per refresh — exactly browser connection
     pool contention.

     New behavior:
       - All videos start with preload="none" and no autoplay (set in
         renderers via data-preload-target/data-autoplay).
       - When a video crosses the viewport rootMargin, it joins a
         FIFO queue.
       - Up to MAX_CONCURRENT videos load at once. Each one is
         considered "done" when canplay fires (buffered enough to
         start playback) OR after a timeout (so a stuck file can't
         hold the queue forever).
       - Once "done", the next queued video begins. */
  function setupLazyVideos() {
    const all = scroller.querySelectorAll('video[data-preload-target]');
    if (!all.length) return;

    const MAX_CONCURRENT = 2;
    const LOAD_TIMEOUT = 8000;
    const queue = [];
    let inFlight = 0;
    const queued = new WeakSet();

    function startLoad(video) {
      const target = video.dataset.preloadTarget || 'metadata';
      const wantsAutoplay = video.dataset.autoplay === '1';
      delete video.dataset.preloadTarget;
      delete video.dataset.autoplay;

      let done = false;
      function finish() {
        if (done) return;
        done = true;
        video.removeEventListener('canplay', finish);
        video.removeEventListener('loadeddata', finish);
        video.removeEventListener('error', finish);
        clearTimeout(timer);
        inFlight--;
        pump();
      }

      video.addEventListener('canplay', finish, { once: true });
      video.addEventListener('loadeddata', finish, { once: true });
      video.addEventListener('error', finish, { once: true });
      const timer = setTimeout(finish, LOAD_TIMEOUT);

      /* Setting preload + autoplay attributes BEFORE calling load()
         lets the browser decide buffering targets correctly. We only
         call play() after canplay, but autoplay attribute also
         triggers it once muted+playsinline are present. */
      video.preload = target;
      if (wantsAutoplay) {
        video.setAttribute('autoplay', '');
      }
      try { video.load(); } catch (_) {}

      if (wantsAutoplay) {
        /* Belt-and-suspenders: also try play() once enough is
           buffered. Browsers are inconsistent about whether the
           autoplay attribute alone triggers playback when set
           after the element exists. */
        video.addEventListener('canplay', function () {
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        }, { once: true });
      }
    }

    function pump() {
      while (inFlight < MAX_CONCURRENT && queue.length) {
        const v = queue.shift();
        inFlight++;
        startLoad(v);
      }
    }

    function enqueue(video) {
      if (queued.has(video)) return;
      queued.add(video);
      queue.push(video);
      pump();
    }

    if (!('IntersectionObserver' in window)) {
      /* Old browsers: enqueue everything in document order; the
         queue's MAX_CONCURRENT cap still protects them from
         flooding the network. */
      all.forEach(enqueue);
      return;
    }

    /* Single observer with a modest rootMargin — we want videos
       to start loading slightly before they're visible, but not
       so far ahead that the whole page queues up at mount time
       (which would defeat the queue). Scroll-videos get the same
       margin; the queue handles their priority just by being
       earlier in the DOM. */
    const io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        enqueue(e.target);
        obs.unobserve(e.target);
      });
    }, { root: scroller, rootMargin: '300px 0px 300px 0px', threshold: 0 });

    all.forEach(function (v) { io.observe(v); });
  }

  /* Scroll-driven videos (type: scroll-video). Playback is mapped
     1:1 to the user's scroll position over the range from when the
     block's top enters the bottom of the viewport to when its
     bottom leaves the top. No play()/pause() calls — we only write
     currentTime, which is the standard pattern for scrub-style
     scroll videos and supports forward + reverse for free. */
  function setupScrollVideos() {
    const videos = scroller.querySelectorAll('video[data-scroll-video]');
    if (!videos.length) return;
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Only scrub videos currently near the viewport. Off-screen
       writes to currentTime trigger seek work we don't need. */
    const active = new Set();
    const io = ('IntersectionObserver' in window)
      ? new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) active.add(e.target);
            else active.delete(e.target);
          });
          schedule();
        }, { root: scroller, rootMargin: '50% 0px 50% 0px', threshold: 0 })
      : null;
    if (io) {
      videos.forEach(function (v) { io.observe(v); });
    } else {
      videos.forEach(function (v) { active.add(v); });
    }

    /* Write currentTime directly to the scroll-mapped position on
       every rAF that follows a scroll. No easing — easing causes
       perceived lag. */
    let pending = false;
    function update() {
      pending = false;
      const scrollerR = scroller.getBoundingClientRect();
      const vh = scroller.clientHeight;
      active.forEach(function (v) {
        const d = v.duration;
        if (!isFinite(d) || d <= 0) return;
        const r = v.getBoundingClientRect();
        /* Top-edge tracking model:
             start_offset = px above the bottom of the viewport where
                            the video's TOP edge sits when frame 0 plays
                            (p = 0 trigger).
             end_offset   = px above the top of the viewport where the
                            video's TOP edge sits when the last frame
                            plays (p = 1 trigger).
           Top of video relative to scroller's top = topY. */
        const topY = r.top - scrollerR.top;
        const startOff = parseInt(v.dataset.scrollStart, 10) || 0;
        const endOff   = parseInt(v.dataset.scrollEnd,   10) || 0;
        const dFirst = vh - startOff;   // topY when p = 0
        const dLast  = -endOff;         // topY when p = 1
        const denom  = dFirst - dLast;
        let p = denom > 0 ? (dFirst - topY) / denom : 0;
        if (p < 0) p = 0; else if (p > 1) p = 1;
        const t = reduceMotion ? 0 : p * d;
        if (Math.abs(v.currentTime - t) < 1e-3) return;
        try { v.currentTime = t; } catch (_) { /* not seekable yet */ }
      });
    }
    function schedule() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(update);
    }
    scroller.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    /* Initial pass once metadata is available so each video shows
       the right frame on first paint. */
    videos.forEach(function (v) {
      if (v.readyState >= 1) schedule();
      else v.addEventListener('loadedmetadata', schedule, { once: true });
    });
  }

  /* Trigger the staggered entrance for every .cs-intro block.
     Two rAFs so the browser commits the initial opacity:0 /
     translateY(16px) state before we add the reveal class —
     otherwise the transition collapses into an instant set. */
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      scroller.querySelectorAll('.cs-intro').forEach(function (el) {
        el.classList.add('cs-intro-revealed');
      });
    });
  });

  /* Cursor-tracking edge highlight on the intro cards (mirrors the
     /work cover hover). Update --cursor-angle and --edge-proximity
     on pointermove; CSS does the rest. Kept inline (8 LOC of math)
     to avoid a shared util — same code lives in src/portfolio.js. */
  function csEdgeProximity(el, x, y) {
    const r = el.getBoundingClientRect();
    const cx = r.width / 2, cy = r.height / 2;
    const dx = x - cx, dy = y - cy;
    const kx = dx !== 0 ? cx / Math.abs(dx) : Infinity;
    const ky = dy !== 0 ? cy / Math.abs(dy) : Infinity;
    return Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
  }
  function csCursorAngle(el, x, y) {
    const r = el.getBoundingClientRect();
    const dx = x - r.width / 2, dy = y - r.height / 2;
    if (!dx && !dy) return 0;
    let deg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    return deg < 0 ? deg + 360 : deg;
  }
  scroller.querySelectorAll('.cs-intro-card').forEach(function (card) {
    card.addEventListener('pointermove', function (e) {
      const r = card.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      card.style.setProperty('--edge-proximity', (csEdgeProximity(card, x, y) * 100).toFixed(2));
      card.style.setProperty('--cursor-angle',   csCursorAngle(card, x, y).toFixed(2) + 'deg');
    });
    card.addEventListener('pointerleave', function () {
      card.style.setProperty('--edge-proximity', '0');
    });
  });

  /* Close button → navigate back to portfolio. */
  if (closeBtn) {
    closeBtn.addEventListener('click', function () {
      if (window.navigateTo) window.navigateTo('/work');
    });
  }

  /* "Back to portfolio" button at the end of the last case study —
     same destination as the close button, delegated so it works
     after innerHTML rebuilds. */
  scroller.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-action="back-to-portfolio"]');
    if (!btn) return;
    if (window.navigateTo) window.navigateTo('/work');
  });

  /* Track which section is currently dominant via a scroll-spy.
     IntersectionObserver doesn't work here because each .cs-section
     is multiple viewport-heights tall (YAML body content stacked
     on top of the 100vh hero), so intersectionRatio caps below
     any useful threshold. Instead, on each scroll frame, find
     the last section whose offsetTop is at or above an anchor
     band ~1/3 of viewport down from the top. Update the URL via
     replaceState and notify the router so the close FLIP targets
     the cover of the visible section, not the originally opened
     one. We don't fire navigateTo / popstate so there's no view
     re-render mid-scroll. */
  const sections = Array.from(scroller.querySelectorAll('.cs-section'));
  let lastSlug = null;
  let scrollSpyPending = false;

  function updateActiveSection() {
    /* Bail when the case-study view isn't visible. Otherwise the
       scroll-spy fires once at IIFE init (scrollTop=0,
       clientHeight=0 because display:none) and rewrites the URL
       to /work/<first-slug>, hijacking the desktop and /work
       routes on initial load. */
    if (!scroller.offsetParent) return;
    const anchor = scroller.scrollTop + Math.max(40, scroller.clientHeight / 3);
    let active = sections[0];
    for (let i = 0; i < sections.length; i++) {
      if (sections[i].offsetTop <= anchor) active = sections[i];
      else break;
    }
    if (!active) return;
    const slug = active.dataset.slug;
    if (!slug || slug === lastSlug) return;
    lastSlug = slug;
    const path = '/work/' + slug;
    if (location.pathname !== path) {
      history.replaceState({ path: path, fromScroll: true }, '', path);
      if (window.__caseStudySlugChanged) window.__caseStudySlugChanged(slug);
    }
  }

  function onScrollSpy() {
    if (scrollSpyPending) return;
    scrollSpyPending = true;
    requestAnimationFrame(function () {
      scrollSpyPending = false;
      updateActiveSection();
    });
  }

  /* Public API used by the router to position the scroller when
     entering the case-study view (cover click or direct URL).
     Registered BEFORE the scroll-spy is attached + seeded so the
     pin-fallback below can land at the requested slug without the
     spy first rewriting the URL to the first section. */
  window.__caseStudyScrollTo = function (slug, behavior) {
    const target = scroller.querySelector('.cs-section[data-slug="' + slug + '"]');
    if (!target) return;
    /* CSS sets scroll-behavior: smooth on .cs-scroller. The
       scrollTo() `behavior: 'auto'` value defers to that CSS,
       which means the scroller animates a long smooth scroll
       when the FLIP needs an instant jump. Setting scrollTop
       directly bypasses the CSS rule entirely; pass
       behavior === 'smooth' explicitly when smooth is wanted. */
    if (behavior === 'smooth') {
      scroller.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
    } else {
      scroller.scrollTop = target.offsetTop;
    }
    lastSlug = slug;
  };

  /* If the user landed directly on /work/<slug> via URL or
     refresh, the router's applyStateImmediate ran while the
     case-study sections were still rendering (its early call to
     window.__caseStudyScrollTo was a no-op because we hadn't
     defined it yet). Now that sections exist AND the function
     is registered, pin the scroller to the requested slug.
     This MUST run before the scroll-spy seeding below — otherwise
     the spy reads scrollTop=0 and replaceState's the URL to the
     first project, which then makes this fallback re-read the
     wrong slug. By scrolling first, the seeding spy reads a
     correct scrollTop and matches lastSlug (which __caseStudy-
     ScrollTo just set), so it bails without touching the URL. */
  if (location.pathname && location.pathname.indexOf('/work/') === 0) {
    const initialSlug = location.pathname.replace(/^\/work\//, '').replace(/\/$/, '');
    if (initialSlug && view.style.display !== 'none') {
      window.__caseStudyScrollTo(initialSlug, 'auto');
    }
  }

  scroller.addEventListener('scroll', onScrollSpy, { passive: true });
  /* Seed lastSlug from the initial scroll position so the very
     first user scroll into a different section fires the update,
     not a redundant one back to the slug we already landed on.
     If the pin-fallback above ran, lastSlug is already set to the
     correct slug and this call short-circuits without rewriting
     the URL. */
  updateActiveSection();

  /* ── JS-driven proximity snap ─────────────────────────────
     Replaces CSS scroll-snap-type. On Chromium/macOS the CSS
     variant defers wheel events through the compositor thread —
     over composited layers (videos, images with border-radius,
     backdrop-filter'd nodes) that manifests as a fully unresponsive
     mechanical scroll wheel until the pointer moves. Doing the snap
     ourselves keeps the "settle on a section boundary at rest"
     feel while letting wheel events dispatch normally. Skipped on
     touch viewports (mobile has its own free-scroll UX). */
  const supportsPointerFine = window.matchMedia('(pointer: fine)').matches;
  if (supportsPointerFine) {
    /* Wait this long after the last scroll event before deciding
       to snap. Trackpad/Magic Mouse gestures fire scroll events at
       ~60Hz until inertia dies, so 150ms comfortably lands after
       the flick has settled but before the user notices any lag. */
    const SNAP_IDLE_MS = 150;
    /* Snap window: half the viewport height. Because sections are
       100vh, the mid-point between any two neighbours is exactly
       0.5vh from each — using 50vh as the threshold means we
       always settle on the nearest section, i.e. mandatory snap. */
    const SNAP_PROXIMITY_PX = Math.round(window.innerHeight * 0.5);
    /* Guard flag: while we're programmatically animating a snap,
       ignore our own emitted scroll events. The animation is
       cancelled naturally if the user re-scrolls (Chromium
       cancels scrollTo({behavior:'smooth'}) on user input). */
    let snapAnimating = false;
    let snapIdleTimer = null;

    function findSnapTarget() {
      const sections = scroller.querySelectorAll('.cs-section');
      const currentTop = scroller.scrollTop;
      let best = null;
      let bestDist = Infinity;
      sections.forEach(function (s) {
        const d = Math.abs(s.offsetTop - currentTop);
        if (d < bestDist) { bestDist = d; best = s; }
      });
      return { section: best, distance: bestDist };
    }

    function attemptSnap() {
      if (snapAnimating) return;
      const { section, distance } = findSnapTarget();
      if (!section) return;
      if (distance < 1) return;                  /* already aligned */
      if (distance > SNAP_PROXIMITY_PX) return;  /* too far away — leave it */
      snapAnimating = true;
      scroller.scrollTo({ top: section.offsetTop, behavior: 'smooth' });
      /* Clear the guard after a generous window — smooth scrolls in
         Chromium/Safari typically finish in ~300ms; 600ms leaves
         headroom without noticeably delaying a rapid follow-up
         gesture. If the user scrolls again inside this window the
         browser cancels our animation and we simply schedule the
         next snap attempt normally. */
      setTimeout(function () { snapAnimating = false; }, 600);
    }

    scroller.addEventListener('scroll', function () {
      if (snapAnimating) return;
      clearTimeout(snapIdleTimer);
      snapIdleTimer = setTimeout(attemptSnap, SNAP_IDLE_MS);
    }, { passive: true });
  }

  });  /* end __projectsReady.then for case-study render */

}
